import React, { useMemo } from 'react';
import { FiberManualRecord, WarningAmber } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useLocation } from 'react-router-dom';
import {
  breakpointAnalysisUrl,
  captableUrl,
  cashFlowLedgerUrl,
  fundOwnershipUrl,
} from 'common/config/urls';
import {
  BREAKPOINT_ANALYSIS_SLUG,
  BREAKPOINT_ANALYSIS_TITLE,
  CAP_TABLE_SLUG,
  CAP_TABLE_TITLE,
  CASH_FLOW_LEDGER_SLUG,
  CASH_FLOW_LEDGER_TITLE,
  FUND_OWNERSHIP_SLUG,
  FUND_OWNERSHIP_TITLE,
} from 'common/constants/cap-table';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import type { TertiaryNavigationLinkItem } from 'context/FirmBaseLayoutContext';

const useCapTableTertiaryNavItems = (firmSlug: string, companySlug: string): TertiaryNavigationLinkItem[] => {
  const { search } = useLocation();
  const [{ captableInfo }] = useStore() as unknown as UseStoreValues;

  const breakpointLabel = useMemo((): React.ReactNode => {
    if (captableInfo?.has_unconfigured_custom_breakpoints) {
      return React.createElement(
        React.Fragment,
        null,
        BREAKPOINT_ANALYSIS_TITLE,
        React.createElement(
          Tooltip,
          { title: 'Some securities are missing values in the custom breakpoints configuration' },
          React.createElement(WarningAmber, { fontSize: 'small', color: 'warning', sx: { ml: 0.5 } })
        )
      );
    }
    if (captableInfo?.use_custom_breakpoints) {
      return React.createElement(
        React.Fragment,
        null,
        BREAKPOINT_ANALYSIS_TITLE,
        React.createElement(
          Tooltip,
          { title: 'Custom breakpoints are active' },
          React.createElement(FiberManualRecord, { sx: { ml: 0.5, fontSize: 15, color: 'success.main' } })
        )
      );
    }
    return BREAKPOINT_ANALYSIS_TITLE;
  }, [captableInfo?.has_unconfigured_custom_breakpoints, captableInfo?.use_custom_breakpoints]);

  return useMemo<TertiaryNavigationLinkItem[]>(
    () => [
      { value: CAP_TABLE_SLUG, label: CAP_TABLE_TITLE, to: `${captableUrl(firmSlug, companySlug)}${search}` },
      { value: FUND_OWNERSHIP_SLUG, label: FUND_OWNERSHIP_TITLE, to: `${fundOwnershipUrl(firmSlug, companySlug)}${search}` },
      { value: BREAKPOINT_ANALYSIS_SLUG, label: breakpointLabel, to: `${breakpointAnalysisUrl(firmSlug, companySlug)}${search}` },
      { value: CASH_FLOW_LEDGER_SLUG, label: CASH_FLOW_LEDGER_TITLE, to: `${cashFlowLedgerUrl(firmSlug, companySlug)}${search}` },
    ],
    [firmSlug, companySlug, breakpointLabel, search]
  );
};

export default useCapTableTertiaryNavItems;
